'use client'

import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import { LoopStepper } from '../_components/widgets/CodeVisuals'
import * as EX from './exercises/code-basics'
import * as EXTRA from './exercises/code-extra'

export default function ArraysAndLoops() {
  return (
    <Lesson>
      <Step>
        <H>Lists of things</H>
        <P>
          Machine learning is about <em>lots</em> of data: thousands of examples, millions of numbers. You can’t give each its
          own variable. Instead you use an <strong>array</strong>: one name for a whole list, written in square brackets.
        </P>
        <Exercise id="arrays-intro" title="Arrays" {...EX.arraysIntro} />
        <Callout kind="warn">
          Counting starts at <strong>zero</strong>. The first item is <C>temps[0]</C>, and the last is{' '}
          <C>temps[temps.length - 1]</C>. Every programmer trips over this at least once.
        </Callout>
      </Step>

      <Step>
        <H>Loops: doing something for every item</H>
        <P>
          A <strong>loop</strong> repeats code. The classic <C>for</C> loop has three parts: where to start (
          <C>let i = 0</C>), when to keep going (<C>{'i < xs.length'}</C>), and how to move on (<C>i++</C>, meaning “add 1 to
          i”). Step through one:
        </P>
        <Figure n="1.3" caption="A for-loop walks the array one index at a time, updating a running value. Press Play, or Step through by hand.">
          <LoopStepper />
        </Figure>
        <P>
          There’s also a shorter form when you don’t need the index: <C>{'for (const x of xs) { … }'}</C> hands you each item
          directly.
        </P>
      </Step>

      <Step gate="sum-max">
        <H>Write the two most useful loops</H>
        <P>
          Adding things up and finding the biggest. You’ll need both again and again — the total error of a model, the most
          likely next word.
        </P>
        <Exercise
          id="sum-max"
          title="sum and biggest"
          {...EX.sumMax}
          hints={[
            <>
              Inside the loop: <C>total = total + xs[i]</C> (or the shortcut <C>total += xs[i]</C>).
            </>,
            <>
              For biggest: <C>{'if (x > best) best = x'}</C> inside a loop over the items.
            </>,
          ]}
        />
      </Step>

      <Step gate="squares-count">
        <H>map, filter, reduce</H>
        <P>
          Some loops are so common they have built-in shortcuts. <C>map</C> transforms every item, <C>filter</C> keeps the ones
          that pass a test, and <C>reduce</C> boils a list down to one value.
        </P>
        <Exercise id="map-filter" title="Shortcuts" {...EX.mapFilter} />
        <Exercise
          id="squares-count"
          title="squares and countAbove"
          {...EX.squaresCount}
          hints={[
            <>
              <C>{'xs.map(x => x * x)'}</C> builds the squares.
            </>,
            <>
              Filter first, then count: <C>{'xs.filter(x => x > t).length'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Loops that run until something happens</H>
        <P>
          A <C>for</C> loop is perfect when you know how many rounds you need. Sometimes you don’t — “keep training until the
          error is small enough”. That’s a <strong>while</strong> loop: it repeats as long as its condition stays true.
        </P>
        <Exercise id="while-loop" title="while, break and continue" {...EXTRA.whileLoop} />
        <Callout kind="warn">
          If the condition never becomes false, a while loop runs forever. Make sure something inside the loop moves you
          toward the exit. (If you get it wrong here, nothing breaks: the runner stops your code after 10 seconds.)
        </Callout>
      </Step>

      <Step gate="collatz">
        <H>Write two while loops</H>
        <Exercise
          id="collatz"
          title="firstIndexAbove and collatzSteps"
          {...EXTRA.collatz}
          hints={[
            <>
              Keep an index <C>let i = 0</C>; inside <C>{'while (i < xs.length)'}</C>, return <C>i</C> as soon as{' '}
              <C>{'xs[i] > t'}</C>, otherwise <C>i++</C>.
            </>,
            <>
              <C>n % 2</C> is the <em>remainder</em> after dividing by 2: it’s 0 exactly when <C>n</C> is even. So:{' '}
              <C>{'if (n % 2 === 0) n = n / 2; else n = 3 * n + 1'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['grid', 'q-index']}>
        <H>Lists of lists</H>
        <P>
          An array can hold arrays. A list of rows is a <strong>grid</strong> — a spreadsheet, a black-and-white image, or (as
          you’ll see in Module 3) a <em>matrix</em>, the workhorse of every neural network. You reach in with two indices:{' '}
          <C>grid[row][column]</C>.
        </P>
        <Exercise id="grid" title="Add up a grid" {...EX.grid} hints={[<>Loop over the rows, and inside that loop, loop over the numbers in each row.</>]} />
        <Quiz
          id="q-index"
          question={
            <>
              With <C>const g = [[7, 8], [9, 10], [11, 12]]</C>, what is <C>g[2][0]</C>?
            </>
          }
          options={[
            { label: '11', correct: true },
            { label: '8', why: 'That’s g[0][1]. The first index picks the row.' },
            { label: '9', why: 'That’s g[1][0] — remember, row indices start at 0.' },
            { label: 'undefined', why: 'Row 2 exists: it’s the third row, [11, 12].' },
          ]}
        />
      </Step>

      <Step>
        <H>The heartbeat of training</H>
        <P>
          Every training loop you’ll write looks like this: <em>for</em> many rounds, <em>for</em> every example, compute
          something and adjust. Loops inside loops, adding things up. You now know all of it.
        </P>
      </Step>
    </Lesson>
  )
}
